/**
 * Hardware Events hook using React Relay
 *
 * Fetches event logs for a specific hardware item via listEvents connection.
 *
 * Note: Run `npm run relay` after making changes to GraphQL queries
 * to regenerate the type artifacts.
 */

import { useLazyLoadQuery, fetchQuery } from 'react-relay';
import { useState, useEffect, useCallback, useMemo } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import type {
  useHardwareEventsRelayQuery as UseHardwareEventsRelayQuery,
  EventFilterInput,
  EventSortInput,
} from './__generated__/useHardwareEventsRelayQuery.graphql';

// ============================================================================
// GraphQL Definitions
// ============================================================================

/**
 * Query for hardware events using listEvents connection.
 * Provides keyset pagination and hasNextPage. Deliberately does NOT select
 * `count` — computing the total over the events table is very expensive.
 */
const HardwareEventsQuery = graphql`
  query useHardwareEventsRelayQuery(
    $filter: EventFilterInput
    $first: Int
    $after: String
    $sort: [EventSortInput]
  ) {
    listEvents(filter: $filter, first: $first, after: $after, sort: $sort) {
      edges {
        cursor
        node {
          id
          createdOn
          recordedAt
          type
          message
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

export type { EventFilterInput, EventSortInput };

/**
 * Event type from GraphQL
 */
export interface EventGraphQL {
  id: string;
  createdOn: string;
  recordedAt: string;
  type: string | null;
  message: string | null;
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Wraps a base filter with a hardware.id constraint so listEvents
 * only returns events for the given hardware.
 */
function buildHardwareFilter(
  hardwareId: string,
  baseFilter: EventFilterInput | null
): EventFilterInput {
  const hardwareCondition: EventFilterInput = {
    hardware: { id: { eq: hardwareId } },
  };
  if (!baseFilter) return hardwareCondition;
  return { and: [hardwareCondition, baseFilter] };
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook for fetching hardware events using Relay with Suspense
 *
 * Uses Suspense - wrap with Suspense boundary
 */
export function useHardwareEventsRelay(
  hardwareId: string,
  options?: {
    filter?: EventFilterInput;
    first?: number;
    after?: string | null;
    sort?: EventSortInput[];
  }
) {
  const baseFilter = options?.filter ?? null;
  const filter = buildHardwareFilter(hardwareId, baseFilter);

  const data = useLazyLoadQuery<UseHardwareEventsRelayQuery>(HardwareEventsQuery, {
    filter,
    first: options?.first ?? 100,
    after: options?.after ?? null,
    sort: options?.sort ?? [{ field: 'CREATED_ON', order: 'DESC' }],
  });

  const edges = data.listEvents?.edges ?? [];
  return {
    events: edges.map((e) => e.node) as EventGraphQL[],
    hasNextPage: data.listEvents?.pageInfo.hasNextPage ?? false,
    endCursor: data.listEvents?.pageInfo.endCursor ?? null,
  };
}

// ============================================================================
// Non-Suspense Hook (with loading state)
// ============================================================================

/**
 * Hook that provides React Query-like interface for hardware events
 * using listEvents connection with keyset pagination.
 *
 * @example
 * const { data, hasNextPage, endCursor, isLoading } =
 *   useHardwareEventsRelayQuery(hardwareId, { first: 25 });
 */
export function useHardwareEventsRelayQuery(
  hardwareId: string | undefined,
  options?: {
    enabled?: boolean;
    filter?: EventFilterInput;
    first?: number;
    after?: string | null;
    sort?: EventSortInput[];
  }
) {
  const [data, setData] = useState<EventGraphQL[]>([]);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0);

  const enabled = options?.enabled !== false && Boolean(hardwareId);
  const first = options?.first ?? 100;
  const after = options?.after ?? null;
  const baseFilter = useMemo(() => options?.filter ?? null, [options?.filter]);
  const sort = useMemo(
    () => options?.sort ?? [{ field: 'CREATED_ON' as const, order: 'DESC' as const }],
    [options?.sort]
  );

  useEffect(() => {
    if (!enabled || !hardwareId) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const doFetch = async () => {
      try {
        setIsLoading(true);

        const filter = buildHardwareFilter(hardwareId, baseFilter);

        const result = await fetchQuery<UseHardwareEventsRelayQuery>(
          relayEnvironment,
          HardwareEventsQuery,
          { filter, first, after, sort }
        ).toPromise();

        if (cancelled) return;

        if (result) {
          const edges = result.listEvents?.edges ?? [];
          setData(edges.map((e) => e.node) as EventGraphQL[]);
          setHasNextPage(result.listEvents?.pageInfo.hasNextPage ?? false);
          setEndCursor(result.listEvents?.pageInfo.endCursor ?? null);
          setError(null);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error('Failed to fetch events'));
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsFetched(true);
        }
      }
    };

    doFetch();

    return () => {
      cancelled = true;
    };
  }, [enabled, hardwareId, baseFilter, first, after, sort, fetchCount]);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  return useMemo(
    () => ({
      data,
      hasNextPage,
      endCursor,
      isLoading,
      isFetched,
      error,
      refetch,
    }),
    [data, hasNextPage, endCursor, isLoading, isFetched, error, refetch]
  );
}
